import { ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import {
  CimClass,
  EquipmentStatus,
  EquipmentTemplateDto,
  NetworkStructure,
  SubstationTemplateDto,
} from '../../../models/network-structure.model';
import { Voltage } from '../../../enums/voltage';
import { ActivatedRoute, Router } from '@angular/router';
import { SubstationTemplateService } from '../../../services/substation-template.service';
import { v4 as uuidv4 } from 'uuid';
import { ExpandedSchematicDiagramComponent } from './expanded-schematic-diagram/expanded-schematic-diagram.component';
import { UtilitySharedService } from '../../../services/shared/utility-shared.service';
import { EquipmentTemplateService } from '../../../services/equipment-template.service';
import { PermissionsService } from '../../../services/permissions.service';

@Component({
  selector: 'lib-add-equipment-and-connectivity',
  templateUrl: './add-equipment-and-connectivity.component.html',
  styleUrls: ['./add-equipment-and-connectivity.component.scss'],
  standalone: false,
})
export class AddEquipmentAndConnectivityComponent {
  @ViewChild('cyto') cyto!: ExpandedSchematicDiagramComponent;
  CIMClass = CimClass;
  Voltage = Voltage;
  networkStructures: NetworkStructure[] = [];
  selectedSubstationTemplate: SubstationTemplateDto = new SubstationTemplateDto();
  selectedEquipmentName: string = '';
  selectedEquipment: EquipmentTemplateDto = new EquipmentTemplateDto();
  equipmentPopupOpened: boolean = false;
  equipmentPageOpened: boolean = false;
  isSchematicExpanded: boolean = false;
  enableEdit: boolean = false;
  deleteClicked: boolean = false;
  isSchematicOpen: boolean = false;
  closingSchematic: boolean = false;
  newEquipment = new EquipmentTemplateDto();
  loadReady: boolean = false;


  utilityId!: number;
  id!: number;

  isDeleteEnabled: boolean = false;

  equipmentList: any[] = [];
  substationTemplates: SubstationTemplateDto[] = [];

  connectorExpanded = false;
  switchgearExpanded = false;
  groundingAndSectioningExpanded = false;
  protectionDevicesExpanded = false;
  comensationEquipmentExpanded = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private ssTemplateService: SubstationTemplateService,
    private cdr: ChangeDetectorRef,
    private utilitySharedService: UtilitySharedService,
    private eqTemplateService: EquipmentTemplateService,
    public permissionsService: PermissionsService
  ) {}

  ngOnInit(): void {
    this.utilityId = Number(this.utilitySharedService.getUtilityId());
    this.id = Number(this.route.snapshot.paramMap.get('id'));
    this.getSubstationTemplate(true);
    this.getAllSubstationTemplates()
  }

  private getAllSubstationTemplates() {
    this.ssTemplateService.getAll(this.utilityId).subscribe({
      next: (response: any) => {
        this.substationTemplates = response?.body?.data;
      }
    })
    this.cdr.detectChanges();
  }

  getSubstationTemplate(init: boolean = false) {
    this.selectedEquipment.substationTemplateId = this.id;
    var obj = { id: this.id, utilityId: this.utilityId };
    this.ssTemplateService.get(obj).subscribe({
      next: (response: any) => {
        this.selectedSubstationTemplate = response?.body?.data;
        this.equipmentList = this.selectedSubstationTemplate.equipmentTemplates;
        
        this.equipmentList.forEach(eq => {
          if (Array.isArray(eq.connectivities)) {
            eq.connectivities.forEach((conn: any) => {
              conn.name = eq.className;
            });
          }
        });

        this.loadReady = true;
        if (
          Array.isArray(this.equipmentList) &&
          this.equipmentList.length > 0
        ) {
          if (init)
            this.selectExistingEquipment(this.equipmentList[0]);
          else
            this.selectExistingEquipment(this.equipmentList.at(-1));
        }
      },
    });
  }

  onAddEquipment() {
    this.equipmentPopupOpened = !this.equipmentPopupOpened;
  }

  closePopup() {
    this.equipmentPopupOpened = false;
  }

  selectExistingEquipment(eq: EquipmentTemplateDto) {
    this.selectedEquipmentName = eq.className;
    this.selectedEquipment = eq;
    this.isDeleteEnabled = true;
    this.enableEdit = false;
    this.cdr.detectChanges();
  }

  onSelectEquipmentType(type: CimClass) {
    var newEquipment = new EquipmentTemplateDto();
    newEquipment.substationTemplateId = this.id;
    newEquipment.utilityId = this.utilityId;
    newEquipment.className = this.getCimLabel(type);
    newEquipment.cimClass = type;
    newEquipment.status = EquipmentStatus.Pending;
    newEquipment.guid = uuidv4();
    newEquipment.visualizationConfiguration.utilityId = this.utilityId;

    this.save(newEquipment);
    this.equipmentPopupOpened = false;
  }

  goBack() {
    if (this.equipmentPageOpened) 
      window.location.reload();
    else {
      this.router.navigate(['../..'], { relativeTo: this.route });
    }
  }

  toggleSchematic() {
    this.isSchematicExpanded = !this.isSchematicExpanded;
  }

  onEdit() {
    this.enableEdit = true;
    this.cdr.detectChanges();
  }

  onDelete() {
    this.deleteClicked = !this.deleteClicked;
  }

  onFormChange() {
    if(this.cyto)
      this.cyto.refreshDiagram();
  }

  openSchematicModal() {
    this.isSchematicOpen = true;
    this.closingSchematic = false;
  }

  closeSchematicModal() {
    this.closingSchematic = true;

    setTimeout(() => {
      this.isSchematicOpen = false;
    }, 500);
  }

  public CimClassLabel: Partial<Record<CimClass, string>> = {
    [CimClass.Substation]: 'Substation',
    [CimClass.PowerTransformer]: 'Power Transformer',
    [CimClass.AcLineSegment]: 'AC Line Segment',
    [CimClass.SwitchGear]: 'Switchgear',
    [CimClass.Conductor]: 'Conductor',
    [CimClass.Busbar]: 'Busbar',
    [CimClass.Junction]: 'Junction',
    [CimClass.Jumper]: 'Jumper',
    [CimClass.Fuse]: 'Fuse',
    [CimClass.Disconnector]: 'Disconnector',
    [CimClass.GroundDisconnector]: 'Ground Disconnector',
    [CimClass.Breaker]: 'Circuit Breaker',
    [CimClass.LoadBreakSwitch]: 'Load Break Switch',
    [CimClass.Ground]: 'Ground',
    [CimClass.Recloser]: 'Recloser',
    [CimClass.Cut]: 'Cut',
    [CimClass.Sectionaliser]: 'Sectionaliser',
    [CimClass.PetersenCoil]: 'Petersen Coil',
    [CimClass.ShuntCompensator]: 'Shunt Compensator',
    [CimClass.FrequencyConverter]: 'Frequency Converter',
    [CimClass.StaticVarCompensator]: 'Static Var Compensator',
  };

  public getCimLabel(c: CimClass): string {
    return this.CimClassLabel[c] ?? (CimClass as any)[c] ?? 'Unknown';
  }

  searchTerm: string = '';

  matchesFilter(label: string): boolean {
    return label.toLowerCase().includes(this.searchTerm.toLowerCase());
  }

  save(obj: EquipmentTemplateDto) {
    this.eqTemplateService.save(obj).subscribe({
      next: (response: any) => {
        this.getSubstationTemplate();
        // window.location.reload();
      },
      error: () => {
        console.log('Error occured while saving equipment.');
      },
    });
  }
}
